import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';
import { FaChurch, FaSearch, FaPhone, FaEnvelope, FaUser, FaMapMarkerAlt } from 'react-icons/fa';
import { directorioVicarias } from '../data/parroquiasDirectorio';
import { MUNICIPIOS_DIOCESIS, municipioDesdeUbicacion } from '../data/municipiosDiocesis';
import {
  oratorioSanFelipeNeri,
  capuchinos,
  scalabrinianos,
  oblatosCorazonesSantísimos,
  comunidadesReligiosas,
} from '../data/vidaConsagrada';
import './Parroquias.css';

const Parroquias = () => {
  const [busqueda, setBusqueda] = useState('');
  const [tabActiva, setTabActiva] = useState('municipios');
  const [vicariaExpandida, setVicariaExpandida] = useState(null);
  const [municipioExpandido, setMunicipioExpandido] = useState(null);

  const filtroParroquia = (p, q) =>
    (p.nombre && p.nombre.toLowerCase().includes(q)) ||
    (p.ubicacion && p.ubicacion.toLowerCase().includes(q)) ||
    (p.email && p.email && p.email.toLowerCase().includes(q)) ||
    (p.telefono && p.telefono.replace(/\s/g, '').includes(q)) ||
    (p.presbiteros && p.presbiteros.some(pr => pr.nombre && pr.nombre.toLowerCase().includes(q)));

  const vicariasAMostrar = useMemo(() => {
    let lista = directorioVicarias
      .slice()
      .sort((a, b) => (a.numero || 0) - (b.numero || 0));
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase().trim();
      lista = lista
        .map(v => ({
          ...v,
          parroquias: v.nombre.toLowerCase().includes(q)
            ? v.parroquias
            : v.parroquias.filter(p => filtroParroquia(p, q)),
        }))
        .filter(v => v.parroquias.length > 0);
    }
    const municipio = (u) => {
      const uu = u || '';
      return uu.includes(' - ') ? uu.split(' - ').pop().trim() : uu;
    };
    return lista.map(v => ({
      ...v,
      parroquias: [...v.parroquias].sort((a, b) => {
        const cmp = municipio(a.ubicacion).localeCompare(municipio(b.ubicacion), 'es');
        return cmp !== 0 ? cmp : (a.nombre || '').localeCompare(b.nombre || '', 'es');
      }),
    }));
  }, [busqueda]);

  const parroquiasPorMunicipio = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const todas = [];
    directorioVicarias.forEach(v => {
      v.parroquias.forEach(p => {
        todas.push({ ...p, vicariaNombre: v.nombre, vicariaNumero: v.numero });
      });
    });
    const conMunicipio = todas
      .map(p => ({ ...p, municipio: municipioDesdeUbicacion(p.ubicacion) }))
      .filter(p => p.municipio != null);
    const filtradas = q
      ? conMunicipio.filter(p =>
          filtroParroquia(p, q) || p.vicariaNombre.toLowerCase().includes(q) || p.municipio.toLowerCase().includes(q)
        )
      : conMunicipio;
    const agrupadas = {};
    MUNICIPIOS_DIOCESIS.forEach(mun => {
      agrupadas[mun] = filtradas.filter(p => p.municipio === mun);
    });
    return agrupadas;
  }, [busqueda]);

  return (
    <div className="parroquias-page">
      <Breadcrumbs />

      <section className="section parroquias-hero">
        <div className="container">
          <h1 className="page-title">Parroquias de la Diócesis</h1>
          <p className="section-intro">
            Directorio por municipio o por vicaría. Busque por nombre de parroquia, ubicación, teléfono, email o presbítero.
          </p>

          <div className="parroquias-tabs">
            <button
              type="button"
              className={`parroquias-tab ${tabActiva === 'municipios' ? 'active' : ''}`}
              onClick={() => setTabActiva('municipios')}
              aria-selected={tabActiva === 'municipios'}
            >
              <FaMapMarkerAlt /> Por municipios
            </button>
            <button
              type="button"
              className={`parroquias-tab ${tabActiva === 'vicaria' ? 'active' : ''}`}
              onClick={() => setTabActiva('vicaria')}
              aria-selected={tabActiva === 'vicaria'}
            >
              <FaChurch /> Por vicaría
            </button>
          </div>

          <div className="parroquias-buscador">
            <div className="search-box parroquias-search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar parroquia, ubicación, teléfono, email o presbítero..."
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                className="search-input"
                aria-label="Buscar parroquias"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section parroquias-listado">
        <div className="container">
          {tabActiva === 'municipios' ? (
            <div className="parroquias-por-municipio">
              {MUNICIPIOS_DIOCESIS.map(mun => {
                const parroquias = parroquiasPorMunicipio[mun] || [];
                if (parroquias.length === 0) return null;
                const abierto = municipioExpandido === mun;
                return (
                  <div key={mun} className="vicaria-block municipio-block">
                    <button
                      type="button"
                      className="vicaria-header"
                      onClick={() => setMunicipioExpandido(abierto ? null : mun)}
                      aria-expanded={abierto}
                    >
                      <FaMapMarkerAlt className="vicaria-icon" />
                      <span className="vicaria-nombre">{mun}</span>
                      <span className="vicaria-count">
                        ({parroquias.length} {parroquias.length === 1 ? 'parroquia' : 'parroquias'})
                      </span>
                    </button>
                    <div className={`vicaria-body ${abierto ? 'open' : ''}`}>
                      <div className="parroquias-tabla-wrapper">
                        <table className="parroquias-tabla parroquias-tabla-directorio" role="grid" aria-label={`Parroquias de ${mun}`}>
                          <thead>
                            <tr>
                              <th scope="col">Parroquia / Zona</th>
                              <th scope="col">Ubicación</th>
                              <th scope="col">Vicaría</th>
                              <th scope="col">Contacto</th>
                              <th scope="col">Presbíteros</th>
                            </tr>
                          </thead>
                          <tbody>
                            {parroquias.map((p, idx) => (
                              <tr key={`${mun}-${idx}`}>
                                <td data-label="Parroquia / Zona">
                                  {p.numero != null && <span className="parroquia-numero">{p.numero}.</span>}
                                  {p.tipo && <span className="parroquia-tipo">{p.tipo}: </span>}
                                  <strong>{p.nombre}</strong>
                                </td>
                                <td data-label="Ubicación">{p.ubicacion}</td>
                                <td data-label="Vicaría">{p.vicariaNombre}</td>
                                <td data-label="Contacto" className="parroquia-contacto">
                                  {p.telefono && (
                                    <span className="parroquia-tel">
                                      <FaPhone className="icon-phone-upright" aria-hidden />{' '}
                                      <a href={`tel:+57${(p.telefono || '').replace(/\D/g, '').slice(-10)}`}>{p.telefono}</a>
                                    </span>
                                  )}
                                  {p.email && (
                                    <span className="parroquia-email">
                                      <FaEnvelope aria-hidden />{' '}
                                      <a href={`mailto:${p.email}`}>{p.email}</a>
                                    </span>
                                  )}
                                  {!p.telefono && !p.email && '—'}
                                </td>
                                <td data-label="Presbíteros" className="parroquia-presbiteros">
                                  {p.presbiteros && p.presbiteros.length > 0 ? (
                                    <ul className="presbiteros-list">
                                      {p.presbiteros.map((pr, i) => (
                                        <li key={i}>
                                          {pr.nombre}
                                          {pr.servicio && <span className="presbitero-servicio"> — {pr.servicio}</span>}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}
              {MUNICIPIOS_DIOCESIS.every(mun => (parroquiasPorMunicipio[mun] || []).length === 0) && (
                <div className="no-results">
                  <p>No se encontraron parroquias con ese criterio.</p>
                </div>
              )}
            </div>
          ) : vicariasAMostrar.length === 0 ? (
            <div className="no-results">
              <p>No se encontraron parroquias con ese criterio.</p>
            </div>
          ) : (
            <div className="parroquias-por-vicaria">
              {vicariasAMostrar.map(vicaria => (
                <div key={vicaria.numero} className="vicaria-block">
                  <button
                    type="button"
                    className="vicaria-header"
                    onClick={() => setVicariaExpandida(vicariaExpandida === vicaria.numero ? null : vicaria.numero)}
                    aria-expanded={vicariaExpandida === null || vicariaExpandida === vicaria.numero}
                  >
                    <FaChurch className="vicaria-icon" />
                    <span className="vicaria-numero">{vicaria.numero}</span>
                    <span className="vicaria-nombre">{vicaria.nombre}</span>
                    <span className="vicaria-count">
                      ({vicaria.parroquias.length} {vicaria.parroquias.length === 1 ? 'parroquia' : 'parroquias'})
                    </span>
                  </button>
                  <div className={`vicaria-body ${(vicariaExpandida === null || vicariaExpandida === vicaria.numero) ? 'open' : ''}`}>
                    {vicaria.vicarioForaneo && (
                      <p className="vicario-foraneo">
                        <FaUser aria-hidden /> <strong>Vicario Foráneo:</strong> {vicaria.vicarioForaneo}
                      </p>
                    )}
                    <div className="parroquias-tabla-wrapper">
                      <table className="parroquias-tabla parroquias-tabla-directorio" role="grid" aria-label={`Parroquias de la Vicaría ${vicaria.nombre}`}>
                        <thead>
                          <tr>
                            <th scope="col">Parroquia / Zona</th>
                            <th scope="col">Ubicación</th>
                            <th scope="col">Contacto</th>
                            <th scope="col">Presbíteros</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vicaria.parroquias.map((p, idx) => (
                            <tr key={`${vicaria.numero}-${idx}`}>
                              <td data-label="Parroquia / Zona">
                                {p.numero != null && <span className="parroquia-numero">{p.numero}.</span>}
                                {p.tipo && <span className="parroquia-tipo">{p.tipo}: </span>}
                                <strong>{p.nombre}</strong>
                              </td>
                              <td data-label="Ubicación">{p.ubicacion}</td>
                              <td data-label="Contacto" className="parroquia-contacto">
                                {p.telefono && (
                                  <span className="parroquia-tel">
                                    <FaPhone className="icon-phone-upright" aria-hidden />{' '}
                                    <a href={`tel:+57${(p.telefono || '').replace(/\D/g, '').slice(-10)}`}>{p.telefono}</a>
                                  </span>
                                )}
                                {p.email && (
                                  <span className="parroquia-email">
                                    <FaEnvelope aria-hidden />{' '}
                                    <a href={`mailto:${p.email}`}>{p.email}</a>
                                  </span>
                                )}
                                {!p.telefono && !p.email && '—'}
                              </td>
                              <td data-label="Presbíteros" className="parroquia-presbiteros">
                                {p.presbiteros && p.presbiteros.length > 0 ? (
                                  <ul className="presbiteros-list">
                                    {p.presbiteros.map((pr, i) => (
                                      <li key={i}>
                                        {pr.nombre}
                                        {pr.servicio && <span className="presbitero-servicio"> — {pr.servicio}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  '—'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Vida consagrada y comunidades religiosas */}
          <div className="vida-consagrada-section">
            <h2 className="vida-consagrada-title">Vida consagrada y comunidades religiosas</h2>

            <div className="vida-consagrada-block">
              <h3 className="vida-consagrada-subtitle">Sacerdotes Congregantes del Oratorio de San Felipe Neri – Ipiales</h3>
              <div className="vida-consagrada-table-wrap">
                <table className="vida-consagrada-table">
                  <thead>
                    <tr><th>Nombre</th><th>Correo electrónico</th><th>Celular</th></tr>
                  </thead>
                  <tbody>
                    {oratorioSanFelipeNeri.map((r, i) => (
                      <tr key={i}>
                        <td>{r.nombre}</td>
                        <td>{r.email && <a href={`mailto:${r.email}`}>{r.email}</a>}</td>
                        <td>{r.celular && <a href={`tel:+57${r.celular.replace(/\D/g, '').slice(-10)}`}>{r.celular}</a>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="vida-consagrada-block">
              <h3 className="vida-consagrada-subtitle">Orden de Hermanos Menores Capuchinos</h3>
              <p className="vida-consagrada-despacho">Despacho: <a href={`tel:+57${capuchinos.despacho.replace(/\D/g, '')}`}>{capuchinos.despacho}</a></p>
              <div className="vida-consagrada-table-wrap">
                <table className="vida-consagrada-table">
                  <thead>
                    <tr><th>Nombre</th><th>Correo electrónico</th><th>Celular</th></tr>
                  </thead>
                  <tbody>
                    {capuchinos.religiosos.map((r, i) => (
                      <tr key={i}>
                        <td>{r.nombre}</td>
                        <td>{r.email && <a href={`mailto:${r.email}`}>{r.email}</a>}</td>
                        <td>{r.celular && <a href={`tel:${r.celular.replace(/\D/g, '')}`}>{r.celular}</a>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="vida-consagrada-block">
              <h3 className="vida-consagrada-subtitle">Misioneros de San Carlos – Scalabrinianos (Hogar de Paso Buen Samaritano)</h3>
              <p className="vida-consagrada-email">E-mail: <a href={`mailto:${scalabrinianos.email}`}>{scalabrinianos.email}</a></p>
              <div className="vida-consagrada-table-wrap">
                <table className="vida-consagrada-table">
                  <thead>
                    <tr><th>Nombre</th><th>Celular</th></tr>
                  </thead>
                  <tbody>
                    {scalabrinianos.religiosos.map((r, i) => (
                      <tr key={i}>
                        <td>{r.nombre}</td>
                        <td>{r.celular && <a href={`tel:+57${r.celular.replace(/\D/g, '').slice(-10)}`}>{r.celular}</a>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="vida-consagrada-block">
              <h3 className="vida-consagrada-subtitle">Congregación de Misioneros Oblatos de los Corazones Santísimos</h3>
              <div className="vida-consagrada-table-wrap">
                <table className="vida-consagrada-table">
                  <thead>
                    <tr><th>Nombre</th><th>Celular</th><th>E-mail</th></tr>
                  </thead>
                  <tbody>
                    {oblatosCorazonesSantísimos.map((r, i) => (
                      <tr key={i}>
                        <td>{r.nombre}</td>
                        <td>{r.celular && <a href={`tel:+57${r.celular.replace(/\D/g, '').slice(-10)}`}>{r.celular}</a>}</td>
                        <td>{r.email && <a href={`mailto:${r.email}`}>{r.email}</a>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="vida-consagrada-block">
              <h3 className="vida-consagrada-subtitle">Comunidades religiosas</h3>
              <div className="vida-consagrada-table-wrap">
                <table className="vida-consagrada-table">
                  <thead>
                    <tr><th>Comunidad</th><th>Superiora</th><th>Celular</th><th>Lugar de servicio</th></tr>
                  </thead>
                  <tbody>
                    {comunidadesReligiosas.map((r, i) => (
                      <tr key={i}>
                        <td>{r.comunidad}</td>
                        <td>{r.superiora}</td>
                        <td>{r.celular && <a href={`tel:+57${r.celular.replace(/\D/g, '').slice(-10)}`}>{r.celular}</a>}</td>
                        <td>{r.lugar}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <p className="parroquias-editar-nota">
            <Link to="/contacto">Sugerir corrección o actualización</Link> del directorio de parroquias.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Parroquias;
